/**
 * 表格提取工具 — 从文本或 DOM 中识别表格结构
 *
 * MVP 方案：基于文本模式的表格检测（制表符/连续空格对齐）。
 * 完整方案需 PDF 表格识别 API（v2）。
 *
 * 对齐 features.md 1.18
 */

/** 表格数据结构 */
export interface TableData {
  headers: string[];
  rows: string[][];
}

/**
 * 检测文本是否是表格行（含多个连续 tab 或 3+ 连续空格）
 */
export function isTableRow(text: string): boolean {
  return /\t.+\t/.test(text) || /\S{2,}\s{3,}\S{2,}/.test(text);
}

/**
 * 从文本块提取表格
 * 输入：多行文本（每行是一行表格数据）
 */
export function extractTable(lines: string[]): TableData | null {
  if (lines.length < 2) return null;

  // 用 tab 或 3+ 空格分割列
  const splitRow = (line: string) =>
    line.split(/\t|\s{3,}/).map((cell) => cell.trim()).filter((cell) => cell.length > 0);

  const parsed = lines.map(splitRow).filter((row) => row.length >= 2);
  if (parsed.length < 2) return null;

  // 第一行作为表头
  const headers = parsed[0];
  const rows = parsed.slice(1);

  // 确保所有行列数一致（填充空值）
  const colCount = headers.length;
  const normalizedRows = rows.map((row) => {
    while (row.length < colCount) row.push('');
    return row.slice(0, colCount);
  });

  return { headers, rows: normalizedRows };
}

/**
 * 从 HTML table 元素提取表格
 */
export function extractTableFromElement(table: Element): TableData | null {
  const headerCells = Array.from(table.querySelectorAll('thead th, tr:first-child th, tr:first-child td'))
    .map((cell) => cell.textContent?.trim() || '');

  const allRows = Array.from(table.querySelectorAll('tr'));
  if (allRows.length === 0) return null;

  let headers: string[];
  let dataRows: Element[];

  if (headerCells.length > 0 && headerCells.some((h) => h)) {
    headers = headerCells;
    dataRows = allRows.slice(1);
  } else {
    // 无明确表头，用第一行作表头
    headers = Array.from(allRows[0].querySelectorAll('td, th')).map((c) => c.textContent?.trim() || `列${1}`);
    dataRows = allRows.slice(1);
  }

  const rows = dataRows.map((tr) =>
    Array.from(tr.querySelectorAll('td, th')).map((cell) => cell.textContent?.trim() || '')
  ).filter((row) => row.length > 0);

  if (rows.length === 0) return null;

  return { headers, rows };
}
